import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, FileText, Award, Clock, Linkedin, CheckCircle2, ExternalLink, ChevronDown } from "lucide-react";
import Reveal from "@/components/Reveal";

export function AboutHero({ a }) {
  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-y-0 right-0 w-1/2 orange-wash opacity-50 pointer-events-none scale-x-[-1]" />
      <div className="max-w-7xl mx-auto px-6 sm:px-10 pt-16 sm:pt-24 pb-20 relative">
        <div className="grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-7">
            <Reveal>
              <p className="font-mono-brand text-xs uppercase tracking-[0.25em] text-[var(--brand-primary)] mb-6">{a.kicker}</p>
              <h1 className="font-display text-5xl sm:text-7xl leading-[0.95] tracking-tight whitespace-pre-line t-heading">
                {a.heading}
              </h1>
              <p className="mt-8 text-lg leading-relaxed text-[var(--brand-muted)] font-medium t-body">{a.p1}</p>
              <p className="mt-5 text-base leading-relaxed text-[var(--brand-muted)] font-medium t-body">{a.p2}</p>
            </Reveal>
          </div>
          <div className="md:col-span-5">
            <Reveal delay={0.15}>
              <div className="relative">
                <div className="absolute -inset-8 bg-[color-mix(in_srgb,var(--brand-primary)_18%,transparent)] rounded-[3rem] blur-3xl" />
                <img
                  src={a.photo}
                  alt={a.heading}
                  fetchPriority="high"
                  decoding="async"
                  className="relative w-full h-[540px] object-cover object-top rounded-[2.25rem] shadow-[0_30px_60px_-20px_rgba(28,24,21,0.45)]"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

export function AboutBanner({ a }) {
  if (!a.bannerImage) return null;
  return (
    <section className="relative w-full h-[55vh] sm:h-[80vh] overflow-hidden" data-testid="about-banner">
      <img src={a.bannerImage} alt="" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-[color-mix(in_srgb,var(--brand-heading)_55%,transparent)] via-transparent to-transparent" />
      {a.bannerCaption && (
        <div className="absolute bottom-0 left-0 right-0">
          <div className="max-w-7xl mx-auto px-6 sm:px-10 pb-10 sm:pb-14">
            <p className="font-display text-4xl sm:text-7xl text-[var(--brand-cream)] tracking-tight leading-[1]" style={{ textShadow: "0 2px 24px rgba(0,0,0,0.35)" }}>
              {a.bannerCaption}
            </p>
          </div>
        </div>
      )}
    </section>
  );
}

export function AboutTimeline({ a }) {
  return (
    <section className="bg-[var(--brand-surface)]">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-20 sm:py-28">
        <Reveal>
          <h2 className="font-display text-5xl sm:text-7xl tracking-tight mb-16 t-heading">{a.timelineHeading}</h2>
        </Reveal>
        <div className="space-y-px">
          {(a.timeline || []).map((item, i) => (
            <Reveal key={item.t} delay={i * 0.08}>
              <div className="grid md:grid-cols-12 gap-6 py-8 border-t border-[#D8CEBE]">
                <div className="md:col-span-2">
                  <span className="font-display text-2xl text-[var(--brand-primary)]">{item.year}</span>
                </div>
                <div className="md:col-span-10">
                  <h3 className="font-display text-3xl text-[var(--brand-heading)] tracking-tight">{item.t}</h3>
                  <p className="mt-3 text-[var(--brand-muted)] leading-relaxed max-w-2xl font-medium">{item.d}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export function AboutValues({ a }) {
  return (
    <section className="max-w-7xl mx-auto px-6 sm:px-10 py-20 sm:py-28">
      <Reveal>
        <p className="font-mono-brand text-xs uppercase tracking-[0.25em] text-[var(--brand-primary)] mb-4 t-heading">{a.valuesKicker}</p>
        <h2 className="font-display text-5xl sm:text-7xl tracking-tight max-w-2xl leading-[0.96] whitespace-pre-line t-heading">{a.valuesHeading}</h2>
      </Reveal>
      <div className="mt-16 grid md:grid-cols-3 gap-6">
        {(a.values || []).map((v, i) => (
          <Reveal key={v.t} delay={i * 0.1}>
            <div className="bg-[#FBF9F4] border border-[#E7DFD0] rounded-3xl shadow-[0_8px_30px_-14px_rgba(28,24,21,0.16)] p-8 h-full hover:bg-[var(--brand-primary)] hover:text-[var(--brand-cream)] hover:-translate-y-1 transition-all duration-300 group">
              <h3 className="font-display text-2xl tracking-tight">{v.t}</h3>
              <p className="mt-4 text-[var(--brand-muted)] group-hover:text-[color-mix(in_srgb,var(--brand-cream)_90%,transparent)] leading-relaxed font-medium transition-colors">{v.d}</p>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.2}>
        <div className="mt-16 flex flex-wrap gap-4">
          <Link to="/contact" data-testid="about-contact-cta" className="group inline-flex items-center gap-2 bg-[var(--brand-primary)] text-[var(--brand-cream)] px-7 py-4 rounded-full font-bold uppercase tracking-[0.1em] text-sm transition-all hover:bg-[var(--brand-primary-dark)] shadow-[0_10px_30px_-10px_rgba(225,37,27,0.6)]">
            Let's work together <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
          </Link>
          <Link to="/resume" data-testid="about-resume-cta" className="inline-flex items-center gap-2 border border-[var(--brand-heading)] text-[var(--brand-heading)] px-7 py-4 rounded-full font-bold uppercase tracking-[0.1em] text-sm transition-all hover:bg-[var(--brand-heading)] hover:text-[var(--brand-cream)]">
            <FileText size={18} /> Get my resume
          </Link>
        </div>
      </Reveal>
    </section>
  );
}

export function AboutCertifications({ certs }) {
  return (
    <section className="bg-[var(--brand-surface)]" data-testid="about-certifications">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-20 sm:py-28">
        <Reveal>
          <p className="font-mono-brand text-xs uppercase tracking-[0.25em] text-[var(--brand-primary)] mb-4 t-heading">Credentials</p>
          <h2 className="font-display text-5xl sm:text-7xl tracking-tight leading-[0.96] whitespace-pre-line t-heading">{certs.heading || "Certifications"}</h2>
          {certs.subheading && <p className="mt-5 text-lg text-[var(--brand-muted)] font-medium max-w-2xl">{certs.subheading}</p>}
        </Reveal>
        <div className="mt-14 grid md:grid-cols-2 gap-6">
          <Reveal>
            <div className="bg-[#FBF9F4] border border-[#E7DFD0] rounded-3xl shadow-[0_8px_30px_-14px_rgba(28,24,21,0.16)] p-8 h-full">
              <div className="flex items-center gap-2 mb-6">
                <Award size={20} className="text-[var(--brand-primary)]" />
                <h3 className="font-mono-brand text-xs uppercase tracking-[0.14em] text-[var(--brand-heading)]">Completed</h3>
              </div>
              <ul className="space-y-4">
                {(certs.completed || []).map((c, i) => (
                  <li key={c.name} className="flex gap-3 text-[#3F3933] font-medium leading-relaxed">
                    <CheckCircle2 size={18} className="text-[var(--brand-primary)] shrink-0 mt-0.5" />
                    {c.url ? (
                      <a href={c.url} target="_blank" rel="noreferrer" data-testid={`cert-completed-link-${i}`} className="inline-flex items-center gap-1 hover:text-[var(--brand-primary)] hover:underline underline-offset-2">
                        {c.name} <ExternalLink size={13} className="shrink-0" />
                      </a>
                    ) : (
                      <span>{c.name}</span>
                    )}
                  </li>
                ))}
                {(!certs.completed || certs.completed.length === 0) && <li className="text-[var(--brand-muted)]">Coming soon.</li>}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="bg-[#FBF9F4] border border-dashed border-[#C9BCA6] rounded-3xl shadow-[0_8px_30px_-14px_rgba(28,24,21,0.12)] p-8 h-full">
              <div className="flex items-center gap-2 mb-6">
                <Clock size={20} className="text-[#B8902F]" />
                <h3 className="font-mono-brand text-xs uppercase tracking-[0.14em] text-[var(--brand-heading)]">In progress</h3>
              </div>
              {(certs.inProgress || []).length > 0 ? (
                <ul className="space-y-4">
                  {certs.inProgress.map((c, i) => (
                    <li key={c.name} className="flex gap-3 text-[#3F3933] font-medium leading-relaxed">
                      <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#B8902F] shrink-0" />
                      {c.url ? (
                        <a href={c.url} target="_blank" rel="noreferrer" data-testid={`cert-inprogress-link-${i}`} className="inline-flex items-center gap-1 hover:text-[#B8902F] hover:underline underline-offset-2">
                          {c.name} <ExternalLink size={13} className="shrink-0" />
                        </a>
                      ) : (
                        <span>{c.name}</span>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-[var(--brand-muted)] font-medium">Always learning. New certifications are on the way, so check back soon.</p>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export function AboutFaq({ faq }) {
  const [open, setOpen] = useState(0);
  if (!faq?.items?.length) return null;
  return (
    <section className="max-w-7xl mx-auto px-6 sm:px-10 py-20 sm:py-24" data-testid="about-faq">
      <Reveal>
        <p className="font-mono-brand text-xs uppercase tracking-[0.25em] text-[var(--brand-primary)] mb-4 t-heading">{faq.kicker}</p>
        <h2 className="font-display text-5xl sm:text-7xl tracking-tight leading-[0.96] whitespace-pre-line t-heading">{faq.heading}</h2>
      </Reveal>
      <div className="mt-14 max-w-3xl space-y-3">
        {faq.items.map((it, i) => (
          <Reveal key={it.q} delay={(i % 4) * 0.05}>
            <div className={`border rounded-2xl transition-colors duration-300 ${open === i ? "bg-[#FBF9F4] border-[var(--brand-primary)]" : "bg-[#FBF9F4] border-[#E7DFD0]"}`}>
              <button
                data-testid={`faq-toggle-${i}`}
                onClick={() => setOpen(open === i ? -1 : i)}
                className="w-full flex items-center justify-between gap-4 text-left px-6 py-5"
              >
                <span className="font-display text-xl sm:text-2xl tracking-tight text-[var(--brand-heading)]">{it.q}</span>
                <ChevronDown size={20} className={`shrink-0 text-[var(--brand-primary)] transition-transform duration-300 ${open === i ? "rotate-180" : ""}`} />
              </button>
              <div className={`faq-answer-wrap ${open === i ? "open" : ""}`}>
                <div>
                  <p className="px-6 pb-6 text-[var(--brand-muted)] leading-relaxed font-medium" data-testid={`faq-answer-${i}`}>{it.a}</p>
                </div>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

export function AboutConnect({ a, footer }) {
  return (
    <section className="max-w-7xl mx-auto px-6 sm:px-10 py-20 sm:py-24" data-testid="about-connect">
      <Reveal>
        <div className="bg-[#0A66C2] text-[var(--brand-cream)] rounded-[2rem] p-10 sm:p-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-8 shadow-[0_30px_60px_-24px_rgba(10,102,194,0.55)]">
          <div className="flex items-start gap-5">
            <div className="bg-[color-mix(in_srgb,var(--brand-cream)_15%,transparent)] p-3 rounded-2xl"><Linkedin size={26} /></div>
            <div>
              <h3 className="font-display text-3xl sm:text-4xl tracking-tight">{a.connectHeading || "Let's connect."}</h3>
              <p className="mt-3 text-[color-mix(in_srgb,var(--brand-cream)_85%,transparent)] font-medium max-w-xl">{a.connectText}</p>
            </div>
          </div>
          <a
            href={footer?.linkedin || "#"}
            target="_blank"
            rel="noreferrer"
            data-testid="about-linkedin-cta"
            className="inline-flex items-center gap-2 bg-[var(--brand-cream)] text-[#0A66C2] px-7 py-4 rounded-full font-bold uppercase tracking-[0.1em] text-sm whitespace-nowrap transition-all hover:bg-white"
          >
            <Linkedin size={18} /> Connect on LinkedIn
          </a>
        </div>
      </Reveal>
    </section>
  );
}
